import { useEffect, useRef, useState, useCallback } from "react";
import { connect } from "react-redux";
import { toast, ToastContainer } from "react-toastify";

import {
    amzInstiPodUpload,
    filePresignedUrlChangeAmzPod,
    uploadFile,
    amzInstiPodInsert,
} from "../../api";
import Navbar from "../../containers/navbar";
import MainHeader from "../../containers/header";
import Header from "../../containers/content_header";
import ErrorModal from "../../containers/error_modal";
import CommonModal from "../../containers/common_modal";

import "react-datepicker/dist/react-datepicker.css";
import "./amz_pod.css";

const TOAST_CONFIG = {
    position: "bottom-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    theme: "colored",
};

const invokeToast = (msg, type = "error") =>
    type === "success" ? toast.success(msg, TOAST_CONFIG) : toast.error(msg, TOAST_CONFIG);

const AmzPod = ({ loc, widthIsSmall, username }) => {
    const [orderNumber, setOrderNumber] = useState("");
    const [invNumber, setInvNumber] = useState("");
    const [poVerified, setPoVerified] = useState(false);
    const [isPoVerifying, setIsPoVerifying] = useState(false);
    const [isUploadLoading, setIsUploadLoading] = useState(false);
    const [lambdaError, setLambdaError] = useState("");
    const [confirmationData, setConfirmationData] = useState(null);

    const invoiceUploadOpenRef = useRef();
    const invoiceUploadCloseRef = useRef();
    const inputFileRef = useRef();
    const openErrRef = useRef();
    const closeErrRef = useRef();
    const uploadedFileRef = useRef(null);

    const isFormIncomplete = !orderNumber.trim() || !invNumber.trim();

    const clearFields = useCallback(() => {
        setOrderNumber("");
        setInvNumber("");
        setPoVerified(false);
        setConfirmationData(null);
        uploadedFileRef.current = null;
    }, []);

    const openError = useCallback((msg) => {
        setLambdaError(msg);
        openErrRef.current?.click();
    }, []);

    const verifyPo = async () => {
        if (isUploadLoading) {
            invokeToast("Please wait, document upload is in progress");
            return;
        }
        setIsPoVerifying(true);
        try {
            const res = await amzInstiPodUpload({
                order_no: orderNumber,
                invoice_no: invNumber,
                user_id: String(username),
                loc,
            });

            if (!res) throw new Error("Something went wrong");

            if (res.status === "error") {
                invokeToast(res.error_message ?? "Verification failed.");
            } else if (res.status === "success") {
                setPoVerified(true);
            }
        } catch (err) {
            console.error("verifyPo:", err);
            invokeToast("PO verification failed. Please try again.");
        } finally {
            setIsPoVerifying(false);
        }
    };

    const uploadToS3 = async (file) => {
        let presigned;
        try {
            presigned = await filePresignedUrlChangeAmzPod({
                file_name: file.name,
            });
            if (!presigned?.url) {
                throw new Error("Invalid presigned URL response.");
            }
        } catch (err) {
            throw new Error("Failed to get upload URL.");
        }

        try {
            const form = new FormData();
            Object.entries(presigned.fields ?? {}).forEach(([k, v]) => {
                form.append(k, v);
            });
            form.append("file", file);

            const uploaded = await uploadFile(presigned.url, form);
            if (!uploaded) {
                throw new Error("Upload failed.");
            }
            return true;
        } catch (err) {
            throw new Error("Failed to upload file.");
        }
    };

    // Modified to upload and strictly show the "Successfully uploaded" modal right away
    const handleFileChange = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        uploadedFileRef.current = file;
        setIsUploadLoading(true);

        try {
            await uploadToS3(file);

            setConfirmationData({
                fileName: file.name,
                fileSize: file.size,
            });

            // Directly open success modal after S3 upload completion
            invoiceUploadOpenRef.current?.click();
        } catch (err) {
            openError(err.message || "Upload failed.");
        } finally {
            setIsUploadLoading(false);
            if (inputFileRef.current) {
                inputFileRef.current.value = "";
            }
        }
    };

    // Form submission processing on OK / Proceed click
    const handleSubmitAfterUpload = async () => {
        try {
            const file = uploadedFileRef.current;
            const payload = {
                user_id: String(username),
                loc,
                po_number: orderNumber,
                invoice_number: invNumber,
                file_name: file?.name,
                file_size_kb: file?.size,
                items: [], // Sent empty as Gemini processing is bypassed
            };

            const res = await amzInstiPodInsert(payload);

            if (!res) throw new Error("Something went wrong");
            if (res.status === "error") {
                throw new Error(res.error_message || "Submission failed.");
            }

            invoiceUploadCloseRef.current?.click();
            invokeToast("File uploaded and submitted successfully", "success");
            clearFields();
        } catch (err) {
            invoiceUploadCloseRef.current?.click();
            openError(err.message || "Failed to submit.");
        }
    };

    const InvoiceUploaded = () => (
        <div className="d-flex flex-column align-items-center mx-5">
            <div className="warn-text">File uploaded successfully.</div>
            &nbsp;
            <button
                className="btn btn-preview mt-3"
                onClick={handleSubmitAfterUpload}
            >
                Proceed
            </button>
        </div>
    );

    const ErrorFooter = () => (
        <div className="d-flex flex-column mb-4">
            {/* <button
                className="btn btn-proceed ms-auto"
                onClick={() => {
                    closeErrRef.current?.click();
                    clearFields();
                }}
            >
                Proceed
            </button> */}
        </div>
    );

    const containerPadding = widthIsSmall ? "0px" : "8px 24px";
    const fullWidth = widthIsSmall ? "100%" : "auto";

    return (
        <>
            <MainHeader />
            <Navbar />

            <div className="up-inv d-flex flex-column w-100" style={{ padding: containerPadding }}>
                <Header progressBar={false} headerText="Amazon POD" />

                <div className="up-inv-form">
                    <div className={widthIsSmall ? "" : "form w-50"}>
                        <div className="d-flex flex-column form-field">
                            <div className="d-flex justify-content-between align-items-center">
                                <label className="sf-label">Order No.</label>
                                <input
                                    className="form-control w-50"
                                    type="text"
                                    value={orderNumber}
                                    onChange={(e) => setOrderNumber(e.target.value)}
                                    disabled={poVerified}
                                />
                            </div>
                        </div>
                    </div>

                    <div className={widthIsSmall ? "" : "form w-50"}>
                        <div className="d-flex flex-column form-field">
                            <div className="d-flex justify-content-between align-items-center">
                                <label className="sf-label">Invoice No.</label>
                                <input
                                    className="form-control w-50"
                                    type="text"
                                    value={invNumber}
                                    onChange={(e) => setInvNumber(e.target.value)}
                                    disabled={poVerified}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="d-flex align-items-baseline flex-column flex-lg-row flex-xl-row flex-md-row">
                        <button
                            className="btn btn-preview d-flex align-items-center justify-content-center"
                            disabled={isPoVerifying || isFormIncomplete}
                            onClick={verifyPo}
                            style={{ width: fullWidth, marginBottom: widthIsSmall ? "10px" : 0, marginTop: widthIsSmall ? "8px" : 0 }}
                        >
                            Verify
                            {isPoVerifying && <div className="spinner ms-3" />}
                        </button>

                        <div
                            className="position-relative mt-xl-5 mt-lg-5 mt-md-5 ms-xl-5 ms-lg-5 ms-md-5"
                            style={{ width: fullWidth }}
                        >
                            <button
                                className="btn btn-upload-file me-5 d-flex align-items-center justify-content-center"
                                disabled={!poVerified || isUploadLoading || isFormIncomplete}
                                style={{ width: fullWidth }}
                            >
                                {isUploadLoading ? "Uploading..." : "Upload Invoice PDF"}
                            </button>

                            <input
                                ref={inputFileRef}
                                className="upload-invoice"
                                type="file"
                                accept=".pdf,application/pdf"
                                onChange={handleFileChange}
                                disabled={!poVerified || isUploadLoading || isFormIncomplete}
                                style={{ width: widthIsSmall ? "100%" : "165px" }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Success modal tied directly to handleSubmitAfterUpload */}
            <CommonModal
                modalCloseRef={invoiceUploadCloseRef}
                modalOpenRef={invoiceUploadOpenRef}
                id="invoiceUploadModal"
                content={<InvoiceUploaded />}
                header
                headerBorder={false}
            />

            <ErrorModal
                id="errorModal"
                openRef={openErrRef}
                closeRef={closeErrRef}
                headerColor="red"
                msg={lambdaError}
                contents={<ErrorFooter />}
                pb={false}
                backDrop
            />

            <ToastContainer
                position="bottom-right"
                autoClose={5000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
            />
        </>
    );
};

const mapStateToProps = (state) => ({
    loc: state?.auth?.user?.loc,
    locationName: state?.auth?.user?.locationName,
    widthIsSmall: state?.auth?.widthIsSmall,
    username: state?.auth?.user?.loginName,
});

export default connect(mapStateToProps)(AmzPod);